import type { ThemeState } from './theme.types';

const STORAGE_KEY = 'idt-theme';

const DEFAULT_STATE: ThemeState = {
  preset: 'Aura',
  primaryColor: 'indutrust',
  surfaceColor: 'instrument',
  darkTheme: false,
  menuMode: 'static',
};

/**
 * Shape of the persisted blob. Older installs may still carry `preset`,
 * `primaryColor`, and `surfaceColor` from the retired rainbow configurator —
 * those fields are ignored on read.
 */
type PersistedThemeBlob = {
  darkTheme?: unknown;
  menuMode?: unknown;
};

/**
 * Reads raw persisted state from `localStorage`.
 *
 * Returns an empty object when nothing is stored, parsing fails, or the
 * stored value isn't an object. Never throws — the app must always boot.
 */
function loadPersistedState(): PersistedThemeBlob {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

/**
 * Validates the user-controlled slice of persisted state.
 *
 * Invalid or stale entries are silently dropped. Authored fields (preset,
 * primary, surface) are never restored from storage.
 */
function validatePersistedState(
  persisted: PersistedThemeBlob
): Pick<Partial<ThemeState>, 'darkTheme' | 'menuMode'> {
  const validated: Pick<Partial<ThemeState>, 'darkTheme' | 'menuMode'> = {};

  if (typeof persisted.darkTheme === 'boolean') {
    validated.darkTheme = persisted.darkTheme;
  }

  if (persisted.menuMode === 'static' || persisted.menuMode === 'overlay') {
    validated.menuMode = persisted.menuMode;
  }

  return validated;
}

/**
 * Reads the OS/browser color-scheme preference.
 *
 * Returns `false` when `matchMedia` is missing or throws — light is the
 * last-resort fallback (same as the hardcoded default).
 */
function prefersDarkColorScheme(): boolean {
  try {
    if (typeof globalThis.matchMedia !== 'function') {
      return false;
    }
    return globalThis.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch {
    return false;
  }
}

/**
 * Returns the initial theme state for store construction.
 *
 * When `idt-theme` has no valid boolean `darkTheme`, the OS/browser
 * `prefers-color-scheme` preference is used. A persisted boolean always
 * wins. There is no live "system" mode — the first persist snapshots the
 * choice and later OS changes are ignored.
 *
 * Authored identity (Aura + brand primary + instrument surface) is always
 * taken from {@link DEFAULT_STATE}; leftover rainbow-picker values in
 * localStorage are ignored.
 */
export function getInitialState(
  brandPrimaryName = DEFAULT_STATE.primaryColor
): ThemeState {
  return {
    ...DEFAULT_STATE,
    primaryColor: brandPrimaryName,
    darkTheme: prefersDarkColorScheme(),
    ...validatePersistedState(loadPersistedState()),
  };
}

/**
 * Writes theme state to `localStorage`. Silently ignores quota errors —
 * failing to persist should never break the app.
 *
 * Persists the full {@link ThemeState} for forward compatibility, but only
 * `darkTheme` and `menuMode` are read back.
 */
export function persistState(state: ThemeState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // quota exceeded – silently ignore
  }
}
